/** Super-admin: choose which AI vendor answers the platform's prompts.
 *
 * The choice applies to every tenant at once and takes effect on the next
 * AI request — no redeploy. Two things are surfaced deliberately rather
 * than hidden, because both cause confusing support tickets otherwise:
 * whether a vendor has a key on the server, and whether it can guarantee
 * the response format our graders expect.
 */
import { useEffect, useState } from "react";
import { ApiError } from "../../api/http";
import * as api from "../../api/platform";
import type { AiSettings } from "../../api/platform";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, Label, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

export function AiSettingsPage() {
  const t = useT();
  const { push } = useToast();
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .getAiSettings()
      .then((s) => {
        setSettings(s);
        setChoice(s.provider);
      })
      .catch(() => setSettings(null));
  }, []);

  if (!settings) return <Spinner />;

  const selected = settings.providers.find((p) => p.id === choice);
  const dirty = choice !== settings.provider;

  const onSave = async () => {
    setBusy(true);
    try {
      const updated = await api.setAiProvider(choice);
      setSettings(updated);
      setChoice(updated.provider);
      push("success", t("ai.saved"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("ai.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t("ai.title")}</h1>
        <p className="text-sm text-muted">{t("ai.subtitle")}</p>
      </div>

      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="ai-provider">{t("ai.provider")}</Label>
            <Select
              id="ai-provider"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            >
              {settings.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.recommended ? ` — ${t("ai.recommended")}` : ""}
                  {p.configured ? "" : ` — ${t("ai.noKey")}`}
                </option>
              ))}
            </Select>
          </div>

          {selected && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={selected.configured ? "success" : "warning"}>
                {selected.configured ? t("ai.keyPresent") : t("ai.keyMissing")}
              </Badge>
              <Badge tone={selected.strict_schema ? "success" : "warning"}>
                {selected.strict_schema ? t("ai.strictSchema") : t("ai.looseSchema")}
              </Badge>
            </div>
          )}

          {selected && !selected.configured && (
            <p className="text-sm text-warning">{t("ai.noKeyHelp")}</p>
          )}
          {selected && !selected.strict_schema && (
            <p className="text-sm text-muted">{t("ai.looseSchemaHelp")}</p>
          )}

          <div>
            <Button onClick={onSave} disabled={!dirty || busy}>
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
